import React from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { InfoIcon } from "../../../../../svg/info-icon";
import { PencilIcon } from "../../../../../svg/pencil-Icon";
import { useIsPrimaryDiagnosisValidated } from "../../../hooks/useIsPrimaryDiagnosisValidated";

/** Props for the primary ICD-10 code header segment. */
interface PrimaryIcdCodeProps {
  /** Display-ready ICD-10 code string (already joined/formatted). */
  formattedIcdCode: string;
  /** Whether the Dx code map is currently refreshing. */
  isRefreshing: boolean;
  /** Whether the logged-in user belongs to an external facility. */
  isExternalFacility: boolean;
  /** Invoked when the edit (pencil) affordance is clicked. */
  onEditIcdClick?: () => void;
}

/**
 * Renders the primary ICD-10 code segment of the authorization header.
 *
 * Validation gating is derived from {@link useIsPrimaryDiagnosisValidated}.
 * For non-external users, when validation is required but the primary Dx is not
 * yet validated, a red info icon is shown; validated orders show no icon. The
 * segment also renders the formatted code, an edit affordance, and a loading
 * spinner while the Dx code map refreshes.
 *
 * @param props - Formatted code, refresh/facility flags, and edit handler.
 * @returns The primary ICD-10 code header segment.
 */
export const PrimaryIcdCode: React.FC<PrimaryIcdCodeProps> = ({
  formattedIcdCode,
  isRefreshing,
  isExternalFacility,
  onEditIcdClick,
}) => {
  const isPrimaryDiagnosisValidated = useIsPrimaryDiagnosisValidated();
  return (
    <div className="flex items-center gap-1 border-l border-primaryGray-14 pl-2 text-sm">
      {isExternalFacility ||
      !isPrimaryDiagnosisValidated.isValidationRequired ? null : (
        <>
          {isPrimaryDiagnosisValidated.isValidated ? null : (
            <InfoIcon width="16px" height="16px" stroke="#CD0202" />
          )}
        </>
      )}
      <span>Primary ICD-10 Code: </span>
      {!isRefreshing && (
        <>
          <span className="text-h12 font-normal leading-h12 text-primaryGray-6">
            {formattedIcdCode}
          </span>
          {isExternalFacility ? null : (
            <PencilIcon
              width={12}
              height={12}
              onClick={onEditIcdClick}
              aria-label="Edit ICD Code"
              className="cursor-pointer transition-transform hover:scale-110"
            />
          )}
        </>
      )}
      {isRefreshing && <SpinningLoader />}
    </div>
  );
};

export default PrimaryIcdCode;
